import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCheck, StickyNote, FileText } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import {
  listCandidates, upsertCandidate, deleteCandidate, moveCandidateStage,
  STAGE_LABELS, ENGLISH_LABELS, SOURCES,
  type Candidate, type CandidateStage, type EnglishProficiency,
} from "@/lib/hr";
import { hireCandidate } from "@/lib/rpc/hire-candidate.functions";
import { todayIstIso } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/recruitment")({
  component: RecruitmentPage,
  head: () => ({
    meta: [{ title: "Recruitment — HR" }, { name: "description", content: "Candidate pipeline" }],
  }),
});

const STAGES: CandidateStage[] = ["applied", "call_screening", "round_1", "round_2", "final", "hired", "rejected"];
const ENG: EnglishProficiency[] = ["beginner", "intermediate", "advanced", "fluent"];

const ROLE_OPTIONS: string[] = [
  "Media Buyer",
  "Video Editor",
  "Senior Video Editor",
  "Graphic Designer",
  "AI Graphic Designer",
  "Scriptwriter",
  "Caller",
  "SEO",
  "SMM",
  "HR",
];

const PILL_BASE: React.CSSProperties = {
  borderRadius: 9999,
  padding: "2px 10px",
  fontSize: 12,
  fontWeight: 500,
  display: "inline-block",
  lineHeight: 1.4,
};

const STAGE_COLORS: Record<CandidateStage, { bg: string; fg: string }> = {
  applied: { bg: "#F3F4F6", fg: "#374151" },
  call_screening: { bg: "#EFF6FF", fg: "#2563EB" },
  round_1: { bg: "#F5F3FF", fg: "#7C3AED" },
  round_2: { bg: "#FDF4FF", fg: "#9333EA" },
  final: { bg: "#F5F3FF", fg: "#6D28D9" },
  hired: { bg: "#DCFCE7", fg: "#16A34A" },
  rejected: { bg: "#FEE2E2", fg: "#DC2626" },
};

function StagePill({ stage }: { stage: CandidateStage }) {
  const c = STAGE_COLORS[stage];
  return (
    <span style={{ ...PILL_BASE, backgroundColor: c.bg, color: c.fg }}>
      {STAGE_LABELS[stage]}
    </span>
  );
}

const ENGLISH_COLORS: Record<EnglishProficiency, { bg: string; fg: string }> = {
  beginner: { bg: "#FEE2E2", fg: "#DC2626" },
  intermediate: { bg: "#FEF9C3", fg: "#CA8A04" },
  advanced: { bg: "#EFF6FF", fg: "#2563EB" },
  fluent: { bg: "#DCFCE7", fg: "#16A34A" },
};

function EnglishPill({ value }: { value: EnglishProficiency | null }) {
  if (!value) return <span className="text-[10px] text-muted-foreground">—</span>;
  const c = ENGLISH_COLORS[value];
  return (
    <span style={{ ...PILL_BASE, backgroundColor: c.bg, color: c.fg }}>
      {ENGLISH_LABELS[value]}
    </span>
  );
}

function ratingColor(r: number): string {
  if (r <= 4) return "#DC2626";
  if (r <= 6) return "#CA8A04";
  if (r <= 8) return "#2563EB";
  return "#16A34A";
}

function humanizeRole(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface FormState {
  id?: string;
  name: string;
  role_applied: string;
  source: string;
  phone: string;
  email: string;
  applied_date: string;
  stage: CandidateStage;
  rating: string;
  english_proficiency: EnglishProficiency | "";
  interviewer_ids: string[];
  notes: string;
  resume_url: string;
}

const emptyForm = (): FormState => ({
  name: "",
  role_applied: "",
  source: "",
  phone: "",
  email: "",
  applied_date: todayIstIso(),
  stage: "applied",
  rating: "",
  english_proficiency: "",
  interviewer_ids: [],
  notes: "",
  resume_url: "",
});

function RecruitmentPage() {
  const qc = useQueryClient();
  const { profile, role } = useAuth();
  const isHrOrAdmin = role === "admin" || profile?.department === "hr";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<CandidateStage | "all">("all");
  const [hireFor, setHireFor] = useState<Candidate | null>(null);

  const { data: candidates = [], isLoading } = useQuery({
    queryKey: ["candidates"],
    queryFn: listCandidates,
  });

  const { data: hrUsers = [] } = useQuery({
    queryKey: ["hr-interviewers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, department")
        .eq("status", "active")
        .in("department", ["hr", "admin"]);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; full_name: string; department: string }>;
    },
  });

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      return upsertCandidate({
        id: f.id,
        name: f.name.trim(),
        role_applied: f.role_applied || null,
        source: f.source || null,
        phone: f.phone || null,
        email: f.email || null,
        applied_date: f.applied_date,
        stage: f.stage,
        rating: f.rating === "" ? null : Number(f.rating),
        english_proficiency: (f.english_proficiency || null) as EnglishProficiency | null,
        interviewer_ids: f.interviewer_ids,
        notes: f.notes || null,
        resume_url: f.resume_url.trim() || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      setOpen(false);
      setForm(emptyForm());
      toast.success("Saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stageMut = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: CandidateStage }) => moveCandidateStage(id, stage),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["candidates"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: deleteCandidate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      toast.success("Deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const stageCounts = STAGES.reduce<Record<string, number>>((acc, s) => {
    acc[s] = candidates.filter((c) => c.stage === s).length;
    return acc;
  }, {});

  const filtered = candidates.filter((c) => {
    if (stageFilter !== "all" && c.stage !== stageFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(s) ||
      (c.role_applied ?? "").toLowerCase().includes(s) ||
      (c.email ?? "").toLowerCase().includes(s)
    );
  });

  const openEdit = (c: Candidate) => {
    setForm({
      id: c.id,
      name: c.name,
      role_applied: c.role_applied ?? "",
      source: c.source ?? "",
      phone: c.phone ?? "",
      email: c.email ?? "",
      applied_date: c.applied_date,
      stage: c.stage,
      rating: c.rating == null ? "" : String(c.rating),
      english_proficiency: c.english_proficiency ?? "",
      interviewer_ids: c.interviewer_ids ?? [],
      notes: c.notes ?? "",
      resume_url: c.resume_url ?? "",
    });
    setOpen(true);
  };

  const ratingEditable = form.stage !== "applied" && isHrOrAdmin;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Recruitment Pipeline</h1>
          <p className="text-xs text-muted-foreground">{candidates.length} candidates</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search name, role, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-64 text-xs"
          />
          <Button size="sm" onClick={() => { setForm(emptyForm()); setOpen(true); }}>
            <Plus className="mr-1 h-3 w-3" /> Add Candidate
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setStageFilter("all")}
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
            stageFilter === "all"
              ? "border-transparent bg-foreground text-background"
              : "border-border bg-card text-foreground hover:bg-accent"
          }`}
        >
          All <span className="opacity-70">({candidates.length})</span>
        </button>
        {STAGES.map((s) => {
          const c = STAGE_COLORS[s];
          const active = stageFilter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStageFilter(s)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                active
                  ? "border-transparent bg-foreground text-background"
                  : "border-border bg-card text-foreground hover:bg-accent"
              }`}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: c.fg }}
              />
              {STAGE_LABELS[s]} <span className="opacity-70">({stageCounts[s] ?? 0})</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border bg-card">

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">S.NO</TableHead>
              <TableHead>CANDIDATE NAME</TableHead>
              <TableHead>ROLE APPLIED</TableHead>
              <TableHead>SOURCE</TableHead>
              <TableHead>STAGE</TableHead>
              <TableHead>RATING /10</TableHead>
              <TableHead>ENGLISH</TableHead>
              <TableHead>APPLIED DATE</TableHead>
              <TableHead>INTERVIEWER</TableHead>
              <TableHead className="text-right">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={10} className="text-center text-xs text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-xs text-muted-foreground">No candidates</TableCell></TableRow>
            )}
            {filtered.map((c, idx) => {
              const interviewers = (c.interviewer_ids ?? [])
                .map((id) => hrUsers.find((u) => u.id === id)?.full_name)
                .filter(Boolean)
                .join(", ");
              return (
                <TableRow key={c.id}>
                  <TableCell className="text-xs">{idx + 1}</TableCell>
                  <TableCell className="text-xs font-medium">{c.name}</TableCell>
                  <TableCell className="text-xs">{c.role_applied ? humanizeRole(c.role_applied) : "—"}</TableCell>
                  <TableCell className="text-xs">{c.source || "—"}</TableCell>
                  <TableCell><StagePill stage={c.stage} /></TableCell>
                  <TableCell>
                    {c.rating != null ? (
                      <span style={{ color: ratingColor(Number(c.rating)), fontWeight: 600, fontSize: 12 }}>{c.rating}/10</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell><EnglishPill value={c.english_proficiency} /></TableCell>
                  <TableCell className="text-xs">{c.applied_date}</TableCell>
                  <TableCell className="text-xs max-w-[120px] truncate" title={interviewers}>{interviewers || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {c.notes && c.notes.trim() && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                                <StickyNote className="h-3 w-3 text-amber-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs whitespace-pre-wrap text-xs">
                              {c.notes}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              disabled={!c.resume_url}
                              onClick={() => c.resume_url && window.open(c.resume_url, "_blank")}
                              style={{ color: c.resume_url ? "#7c3aed" : undefined }}
                            >
                              <FileText className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="text-xs">
                            {c.resume_url ? "View Resume" : "No resume added"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 px-2 text-[10px]"
                        disabled={c.stage !== "final"}
                        onClick={() => setHireFor(c)}
                      >
                        <UserCheck className="mr-1 h-3 w-3" /> Hire
                      </Button>
                      <Select
                        value={c.stage}
                        onValueChange={(v) => stageMut.mutate({ id: c.id, stage: v as CandidateStage })}
                      >
                        <SelectTrigger className="h-7 w-[110px] text-[10px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STAGES.map((s) => (
                            <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive"
                        onClick={() => { if (confirm(`Delete ${c.name}?`)) delMut.mutate(c.id); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{form.id ? "Edit Candidate" : "Add Candidate"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 overflow-y-auto px-6 py-4">
            <div className="col-span-2">
              <label className="text-xs font-medium">Candidate Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-xs font-medium">Role Applied</label>
              <Select value={form.role_applied || undefined} onValueChange={(v) => setForm({ ...form, role_applied: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select role…" /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  {form.role_applied && !ROLE_OPTIONS.includes(form.role_applied) && (
                    <SelectItem value={form.role_applied}>{humanizeRole(form.role_applied)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Source</label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-xs font-medium">Email</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 text-xs" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Resume / CV Link</label>
              <Input
                type="url"
                placeholder="https://drive.google.com/... or any direct link"
                value={form.resume_url}
                onChange={(e) => setForm({ ...form, resume_url: e.target.value })}
                className="h-9 text-xs"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Paste a Google Drive, Dropbox, or direct file link (PDF, image, doc)
              </p>
            </div>
            <div>
              <label className="text-xs font-medium">Applied Date</label>
              <Input type="date" value={form.applied_date} onChange={(e) => setForm({ ...form, applied_date: e.target.value })} className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-xs font-medium">Stage</label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v as CandidateStage })}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Rating /10 {ratingEditable ? "" : "(unlock at Call Screening)"}</label>
              <Input
                type="number" min={0} max={10}
                disabled={!ratingEditable}
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: e.target.value })}
                className="h-9 text-xs"
              />
            </div>
            <div>
              <label className="text-xs font-medium">English Proficiency</label>
              <Select
                value={form.english_proficiency || undefined}
                onValueChange={(v) => setForm({ ...form, english_proficiency: v as EnglishProficiency })}
                disabled={!isHrOrAdmin || form.stage === "applied"}
              >
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {ENG.map((e) => <SelectItem key={e} value={e}>{ENGLISH_LABELS[e]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Interviewers</label>
              <div className="flex flex-wrap gap-1.5 rounded-md border p-2">
                {hrUsers.map((u) => {
                  const active = form.interviewer_ids.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          interviewer_ids: active
                            ? form.interviewer_ids.filter((x) => x !== u.id)
                            : [...form.interviewer_ids, u.id],
                        })
                      }
                      className={`rounded-full border px-2 py-0.5 text-[10px] ${active ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}
                    >
                      {u.full_name}
                    </button>
                  );
                })}
                {hrUsers.length === 0 && <span className="text-[10px] text-muted-foreground">No HR/admin users</span>}
              </div>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium">Notes</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-xs" rows={2} />
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t bg-background px-6 py-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.name.trim() || saveMut.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {hireFor && <HireModal candidate={hireFor} onClose={() => setHireFor(null)} />}
    </div>
  );
}

function HireModal({ candidate, onClose }: { candidate: Candidate; onClose: () => void }) {
  const qc = useQueryClient();
  const [employeeId, setEmployeeId] = useState("");
  const [department, setDepartment] = useState("editor");
  const [joiningDate, setJoiningDate] = useState(todayIstIso());
  const [salary, setSalary] = useState("");
  const [password, setPassword] = useState(() =>
    Math.random().toString(36).slice(-10) + "A1!"
  );

  const hireMut = useMutation({
    mutationFn: async () => {
      return hireCandidate({
        data: {
          candidate_id: candidate.id,
          employee_id: employeeId,
          department: department as "admin" | "editor" | "graphic_designer" | "ai" | "marketer" | "seo" | "smm" | "scriptwriter" | "calling" | "hr",
          joining_date: joiningDate,
          salary: salary ? Number(salary) : undefined,
          password,
        },
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["candidates"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["directory"] });
      toast.success(
        `${candidate.name} hired. Temp password: ${password} (share with ${res.email})`,
        { duration: 15000 },
      );
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = /^\d{4,}$/.test(employeeId) && password.length >= 8 && !!candidate.email;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Hire {candidate.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!candidate.email && (
            <div className="rounded border border-destructive/40 bg-destructive/10 p-2 text-[11px] text-destructive">
              Candidate has no email. Edit the candidate to add one before hiring.
            </div>
          )}
          <div>
            <label className="text-xs font-medium">Employee ID *</label>
            <Input
              placeholder="e.g. 1002"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value.replace(/\D/g, ""))}
              className="h-9 font-mono text-xs"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">Min 4 digits, numeric, must be unique</p>
          </div>
          <div>
            <label className="text-xs font-medium">Department *</label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["editor","graphic_designer","ai","marketer","seo","smm","scriptwriter","calling","hr","admin"].map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Joining Date *</label>
            <Input type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} className="h-9 text-xs" />
          </div>
          <div>
            <label className="text-xs font-medium">Salary (monthly)</label>
            <Input type="number" value={salary} onChange={(e) => setSalary(e.target.value)} className="h-9 text-xs" />
          </div>
          <div>
            <label className="text-xs font-medium">Temporary Password *</label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} className="h-9 font-mono text-xs" />
            <p className="mt-1 text-[10px] text-muted-foreground">Share with the new hire so they can log in.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => hireMut.mutate()} disabled={!canSubmit || hireMut.isPending}>
            {hireMut.isPending ? "Creating…" : "Confirm Hire"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
